/**
 * AdMob Interstitial Ad Module
 */

import { AdMob, InterstitialAdPluginEvents, AdLoadInfo } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

// AdMob Ad Unit IDs - milestone_interstitial
const AD_UNIT_IDS = {
  android: 'ca-app-pub-5244313691595903/3401121433',
  ios: 'ca-app-pub-3940256099942544/4411468910', // Test Ad Unit ID (iOS not yet set up)
};

let isAdLoaded = false;
let isAdLoading = false;

/**
 * AdMob Initialization
 */
export async function initializeAdMob(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    console.log('[AdMob] Not a native platform, skipping initialization');
    return;
  }

  try {
    await AdMob.initialize({
      initializeForTesting: false, // Production mode
    });
    console.log('[AdMob] Initialized successfully');

    // Register listeners
    AdMob.addListener(InterstitialAdPluginEvents.Loaded, (info: AdLoadInfo) => {
      console.log('[AdMob] Interstitial loaded', info);
      isAdLoaded = true;
      isAdLoading = false;
    });

    AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (error) => {
      console.error('[AdMob] Interstitial failed to load', error);
      isAdLoaded = false;
      isAdLoading = false;
    });

    AdMob.addListener(InterstitialAdPluginEvents.Showed, () => {
      console.log('[AdMob] Interstitial showed');
    });

    AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
      console.log('[AdMob] Interstitial dismissed');
      isAdLoaded = false;
      // Preload next ad
      preloadInterstitial();
    });

    AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, (error) => {
      console.error('[AdMob] Interstitial failed to show', error);
      isAdLoaded = false;
    });

    // Preload first ad
    await preloadInterstitial();
  } catch (error) {
    console.error('[AdMob] Failed to initialize', error);
  }
}

/**
 * Preload interstitial ad
 */
export async function preloadInterstitial(): Promise<void> {
  if (!Capacitor.isNativePlatform() || isAdLoading || isAdLoaded) {
    return;
  }

  isAdLoading = true;

  const platform = Capacitor.getPlatform();
  const adId = platform === 'android' ? AD_UNIT_IDS.android : AD_UNIT_IDS.ios;

  try {
    await AdMob.prepareInterstitial({
      adId,
    });
    console.log('[AdMob] Interstitial preloaded');
  } catch (error) {
    console.error('[AdMob] Failed to preload interstitial', error);
    isAdLoading = false;
  }
}

/**
 * Show interstitial ad
 * @returns true if ad was shown, false otherwise
 */
export async function showInterstitial(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    console.log('[AdMob] Not a native platform, skipping ad');
    return false;
  }

  if (!isAdLoaded) {
    console.log('[AdMob] Ad not loaded yet');
    // Try loading if not loaded
    await preloadInterstitial();
    return false;
  }

  try {
    await AdMob.showInterstitial();
    console.log('[AdMob] Interstitial shown successfully');
    return true;
  } catch (error) {
    console.error('[AdMob] Failed to show interstitial', error);
    return false;
  }
}
